// Pure helpers for BookingFlow. No React, no state.

const DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

export function getDayOfWeek(dateStr) {
  if (!dateStr) return null;
  return DAY_NAMES[new Date(dateStr).getDay()];
}

export function getOperatingHoursForDate(location, dateStr) {
  if (!location || !dateStr) return null;
  const dow = getDayOfWeek(dateStr);
  return location.operating_hours?.[dow] || null;
}

export function isSlotInBreak(slotStart, breaks) {
  if (!breaks || breaks.length === 0) return false;
  const slotHour = parseInt(slotStart.split(':')[0], 10);
  for (const breakPeriod of breaks) {
    const breakStartHour = parseInt(breakPeriod.start.split(':')[0], 10);
    const breakEndHour = parseInt(breakPeriod.end.split(':')[0], 10);
    if (slotHour >= breakStartHour && slotHour < breakEndHour) return true;
  }
  return false;
}

// Generate hourly time-slot labels ("HH:00-HH:00") between open and close,
// skipping any slot that falls inside a break period. Returns [] when the
// location is closed on the given day.
export function buildTimeSlots(location, dateStr) {
  if (!location || !dateStr) {
    // Default list when nothing's picked yet — matches the previous behaviour.
    return [
      '08:00-09:00', '09:00-10:00', '10:00-11:00',
      '11:00-12:00', '12:00-13:00', '13:00-14:00', '14:00-15:00', '15:00-16:00',
      '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00', '20:00-21:00',
      '21:00-22:00', '22:00-23:00', '23:00-00:00',
    ];
  }
  const hours = getOperatingHoursForDate(location, dateStr);
  if (!hours || hours.closed) return [];

  const openTime = hours.open || '08:00';
  const closeTime = hours.close || '22:00';
  const breaks = hours.breaks || [];
  const [openHour] = openTime.split(':').map(Number);
  let [closeHour] = closeTime.split(':').map(Number);
  if (closeHour === 0) closeHour = 24; // treat midnight as end-of-day

  const slots = [];
  for (let hour = openHour; hour < closeHour; hour++) {
    const start = hour.toString().padStart(2, '0');
    const slotStart = `${start}:00`;
    if (isSlotInBreak(slotStart, breaks)) continue;
    const end = ((hour + 1) % 24).toString().padStart(2, '0');
    slots.push(`${start}:00-${end}:00`);
  }
  return slots;
}

export function getConsecutiveSlots(timeSlots, startSlot, dur) {
  const startIndex = timeSlots.indexOf(startSlot);
  if (startIndex === -1) return [];
  const out = [];
  for (let i = 0; i < dur; i++) {
    if (startIndex + i < timeSlots.length) out.push(timeSlots[startIndex + i]);
  }
  return out;
}

// Brunei-time-aware "is this slot in the past" check. Callers pass the
// currently-known server date/time (both in Brunei tz) so we don't drift
// with the viewer's local clock.
export function isTimeSlotPast(timeSlot, serverDate, serverTime, selectedDate) {
  if (!serverDate || !serverTime) return false;
  if (selectedDate > serverDate) return false;  // future date
  if (selectedDate < serverDate) return true;   // past date
  const slotStartTime = timeSlot.split('-')[0];
  return slotStartTime <= serverTime;
}

export function formatOperatingHoursDisplay(hours) {
  if (!hours || hours.closed) return 'Closed';
  const closeDisplay = hours.close === '00:00' ? '24:00 (Midnight)' : hours.close;
  return `${hours.open} - ${closeDisplay}`;
}
